import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest, isApiError } from './client';
import { queryKeys } from './keys';
import type {
  BookingAccessRequestInput,
  OtpRequestBody,
  OtpResendInput,
  OtpVerifyInput,
} from './types';

/** Signed-in state from GET /auth/me. A 401 means signed out (data = null), not an error. */
export function useSession() {
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: async ({ signal }) => {
      try {
        return await apiRequest('/auth/me', { signal });
      } catch (error) {
        if (isApiError(error) && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export type OtpRequestInput = OtpRequestBody;

export function requestOtp(input: OtpRequestInput) {
  return apiRequest('/auth/otp/request', { method: 'POST', body: input });
}

export function resendOtp(challengeId: string) {
  const input: OtpResendInput = { challengeId };
  return apiRequest('/auth/otp/resend', { method: 'POST', body: input });
}

export function verifyOtp(challengeId: string, code: string) {
  const input: OtpVerifyInput = { challengeId, code };
  return apiRequest('/auth/otp/verify', {
    method: 'POST',
    body: input,
  });
}

export function requestBookingAccess(input: BookingAccessRequestInput) {
  return apiRequest('/bookings/access/request', { method: 'POST', body: input });
}

/** Clears every member-scoped query after sign-in/out so no stale personal data remains. */
export function useResetMemberState() {
  const client = useQueryClient();
  return useCallback(async () => {
    client.removeQueries({ queryKey: ['me'] });
    client.removeQueries({ queryKey: ['booking'] });
    await client.invalidateQueries({ queryKey: queryKeys.session });
    await client.invalidateQueries({ queryKey: ['trips'] });
    await client.invalidateQueries({ queryKey: ['trip'] });
  }, [client]);
}

/** Callers navigate away first, then call `useResetMemberState()` so guards don't redirect to sign-in. */
export function useLogout(everywhere = false) {
  return useMutation({
    mutationFn: () =>
      everywhere
        ? apiRequest('/auth/logout-all', { method: 'POST' })
        : apiRequest('/auth/logout', { method: 'POST' }),
  });
}
